import { useEffect, useMemo, useRef, useState } from "react";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Trash2, Phone, Mail, Building2, Receipt, FileText, Download, Filter, Camera, Loader2, Sparkles, Pencil, Truck, Search, ExternalLink, Wallet, Link2 } from "lucide-react";
import { DdtPagamentoDialog } from "@/components/DdtPagamentoDialog";
import { CollegaDdtFatturaDialog } from "@/components/CollegaDdtFatturaDialog";
import { BulkCollegaDdtDialog } from "@/components/BulkCollegaDdtDialog";
import { DdtPagamentoFields, PAG_DDT_EMPTY, pagDdtCampi, pagDdtDaOcr, registraPagamentoDdt } from "@/components/DdtPagamentoFields";
import { toast } from "sonner";
import { compressImage } from "@/lib/image";
import { useNavigate, useSearchParams } from "react-router-dom";
import { postWithDupCheck } from "@/lib/dupCheck";
import { afterAiFattura, OcrBadges } from "@/components/PagamentoMetodoDialog";
import { reviewMagazzinoItems } from "@/components/MagazzinoReview";
import { DdtRiferitiDialog } from "@/components/DdtRiferitiDialog";
import { FatturaDdtDialog } from "@/components/FatturaDdtDialog";
import { AggiungiDdtDialog } from "@/components/AggiungiDdtDialog";
import { FornitoreEditor, NuovoFornitoreChoice, CopiaIban } from "@/components/FornitoreEditor";

const fmt = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(n || 0);
const emptyFatt = { tipo: "acquisto", numero: "", data: new Date().toISOString().slice(0, 10), imponibile: 0, iva_percentuale: 22, stato: "da_pagare", data_scadenza: "", note: "", cantiere_id: null, mezzo_pagamento: "bonifico" };
const emptyDDT = { numero: "", data: new Date().toISOString().slice(0, 10), descrizione: "", importo_stimato: 0, cantiere_id: null, stato_pagamento: "non_pagato", mezzo_pagamento: "", data_pagamento: "", note_pagamento: "" };

const DDT_PAG_LABELS = { non_pagato: "Non pagato", parziale: "Parziale", pagato: "Pagato" };
const DDT_PAG_COLORS = {
  non_pagato: "bg-red-100 text-red-800 border-red-300",
  parziale: "bg-amber-100 text-amber-800 border-amber-300",
  pagato: "bg-emerald-100 text-emerald-800 border-emerald-300",
};
const MEZZI_DDT = [
  { v: "bonifico", l: "Bonifico" },
  { v: "riba_rid", l: "RIBA / RID" },
  { v: "contanti", l: "Contanti" },
  { v: "assegno", l: "Assegno" },
  { v: "pos", l: "Carta / POS" },
  { v: "altro", l: "Altro" },
];

const MESI = ["Tutti", "Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];

function monthFromISO(iso) {
  if (!iso || !/^\d{4}-\d{2}-\d{2}/.test(iso)) return null;
  return parseInt(iso.slice(5, 7), 10);
}
function yearFromISO(iso) {
  if (!iso || !/^\d{4}-\d{2}-\d{2}/.test(iso)) return null;
  return parseInt(iso.slice(0, 4), 10);
}

export default function Fornitori() {
  const [list, setList] = useState([]);
  const [choiceOpen, setChoiceOpen] = useState(false);
  const [editor, setEditor] = useState(null);
  const [detail, setDetail] = useState(null);
  const [cantieri, setCantieri] = useState([]);
  const [addDoc, setAddDoc] = useState(null);
  const [fattForm, setFattForm] = useState(emptyFatt);
  const [ddtForm, setDdtForm] = useState(emptyDDT);
  const [pagNew, setPagNew] = useState(PAG_DDT_EMPTY);
  const [linkDdt, setLinkDdt] = useState(null);
  const [filtMese, setFiltMese] = useState(0);
  const [filtAnno, setFiltAnno] = useState("");
  const [filtGiorno, setFiltGiorno] = useState("");
  const [ocrBusy, setOcrBusy] = useState(false);
  const [ocrResult, setOcrResult] = useState(null);
  const fileRef = useRef();
  // Tab globale DDT
  const [allDdt, setAllDdt] = useState([]);
  const [ddtFiltData, setDdtFiltData] = useState("");
  const [ddtFiltForn, setDdtFiltForn] = useState("");
  const [ddtFiltCant, setDdtFiltCant] = useState("");
  const [ddtFiltStato, setDdtFiltStato] = useState("");
  const [ddtFiltPag, setDdtFiltPag] = useState("");
  const [editingDdtId, setEditingDdtId] = useState(null);
  const [editDdtForm, setEditDdtForm] = useState(emptyDDT);
  const [searchForn, setSearchForn] = useState("");
  const [mainTab, setMainTab] = useState("rubrica");
  const [highlightDdt, setHighlightDdt] = useState(null);
  const [payDdt, setPayDdt] = useState(null);
  const [selDdt, setSelDdt] = useState([]);
  const [bulkLink, setBulkLink] = useState([]);
  const [aggiungiOpen, setAggiungiOpen] = useState(false);
  const [ddtDialogFid, setDdtDialogFid] = useState(null);
  const [riferiti, setRiferiti] = useState(null);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const did = searchParams.get("ddt");
    if (!did) return;
    setMainTab("ddt"); setHighlightDdt(did); setSearchParams({}, { replace: true });
    setTimeout(() => document.querySelector(`[data-testid="ddt-global-row-${did}"]`)?.scrollIntoView({ block: "center" }), 600);
  }, [searchParams, setSearchParams]);
  const openFatturaLink = (fid) => navigate(`/fatture?open=${fid}`);
  const fornFiltered = list.filter((f) => {
    const s = searchForn.trim().toLowerCase();
    return !s || (f.nome || "").toLowerCase().includes(s) || (f.partita_iva || "").includes(s);
  });

  const load = async () => { const { data } = await api.get("/fornitori"); setList(data); };
  const loadCantieri = async () => { const { data } = await api.get("/cantieri"); setCantieri(data); };
  const loadAllDdt = async () => { const { data } = await api.get("/ddt"); setAllDdt(data || []); };
  useEffect(() => { load(); loadCantieri(); loadAllDdt(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Eliminare fornitore?")) return;
    try { await api.delete(`/fornitori/${id}`); toast.success("Rimosso"); load(); } catch { toast.error("Errore"); }
  };
  const openDetail = async (id) => {
    setFiltMese(0); setFiltAnno(""); setFiltGiorno("");
    try { const { data } = await api.get(`/fornitori/${id}/dettaglio`); setDetail(data); } catch { toast.error("Errore"); }
  };
  const refreshDetail = async () => {
    if (!detail?.fornitore?.id) return;
    const { data } = await api.get(`/fornitori/${detail.fornitore.id}/dettaglio`);
    setDetail(data);
  };

  const saveFatt = async () => {
    try {
      let magItems = fattForm.magazzino_items;
      if (magItems?.length) { magItems = await reviewMagazzinoItems(magItems); if (magItems === null) return; }
      const r = await postWithDupCheck("/fatture", { ...fattForm, magazzino_items: magItems, controparte: detail.fornitore.nome, fornitore_id: detail.fornitore.id });
      if (!r) return;
      toast.success(r.data?.overwritten ? "Fattura sovrascritta" : "Fattura salvata");
      await afterAiFattura(r.data, fattForm);
      if (fattForm.ddt_riferiti?.length) setRiferiti({ fattura: r.data, items: fattForm.ddt_riferiti });
      setFattForm(emptyFatt); setAddDoc(null); setOcrResult(null); refreshDetail();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  const saveDDT = async () => {
    try {
      const r = await postWithDupCheck("/ddt", { ...ddtForm, ...pagDdtCampi(pagNew), fornitore_id: detail.fornitore.id });
      if (!r) return;
      await registraPagamentoDdt(r.data?.id, pagNew);
      setPagNew(PAG_DDT_EMPTY);
      toast.success(r.data?.overwritten ? "DDT sovrascritto" : "DDT salvato");
      setDdtForm(emptyDDT); setAddDoc(null); setOcrResult(null); refreshDetail(); loadAllDdt();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  // OCR: scatta foto -> rileva tipo -> chiede conferma -> apre dialog pre-compilato
  const onPhoto = async (file) => {
    if (!file || !detail?.fornitore?.id) return;
    setOcrBusy(true);
    try {
      const small = await compressImage(file, { maxDim: 1600, quality: 0.85 });
      toast.info("Analisi AI del documento in corso...");
      const fd = new FormData();
      fd.append("file", small);
      fd.append("fornitore_id", detail.fornitore.id);
      const { data } = await api.post("/documenti/ocr", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 90000,
      });
      setOcrResult(data);
      toast.success(`AI: rilevato ${data.tipo_rilevato?.toUpperCase()} (${data.confidenza || 0}% confidenza). Conferma il tipo.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "OCR fallito. Inserisci manualmente.");
    } finally {
      setOcrBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const confirmOcrType = (tipo) => {
    if (!ocrResult) return;
    if (tipo === "fattura") {
      const f = ocrResult.fattura || {};
      setFattForm({
        ...emptyFatt,
        numero: f.numero || "",
        data: /^\d{4}-\d{2}-\d{2}$/.test(f.data || "") ? f.data : emptyFatt.data,
        imponibile: Number(f.imponibile) || 0,
        iva_percentuale: Number(f.iva_percentuale) || 22,
        cantiere_id: f.cantiere_id || null,
        note: f.descrizione || "",
        ddt_riferiti: f.ddt_riferiti || [],
        fornitore_extra: f.fornitore_anagrafica || null,
        stato: f.stato_pagamento === "pagata" ? "pagata" : "da_pagare",
        data_pagamento: f.stato_pagamento === "pagata" && /^\d{4}-\d{2}-\d{2}$/.test(f.data || "") ? f.data : null,
        ai_pagato: f.stato_pagamento === "pagata",
        ai_metodo: f.metodo_pagamento || "",
        magazzino_items: f.magazzino_items?.length ? f.magazzino_items : null,
        allegato_base64: ocrResult.original_base64 || null,
        allegato_mime: ocrResult.original_mime || null,
        allegato_filename: ocrResult.original_filename || null,
      });
      setAddDoc("fattura");
    } else {
      const d = ocrResult.ddt || {};
      setPagNew(pagDdtDaOcr(ocrResult));
      setDdtForm({
        ...emptyDDT,
        numero: d.numero || "",
        data: /^\d{4}-\d{2}-\d{2}$/.test(d.data || "") ? d.data : emptyDDT.data,
        descrizione: d.descrizione || "",
        importo_stimato: Number(d.importo_stimato) || 0,
        cantiere_id: d.cantiere_id || null,
      });
      setAddDoc("ddt");
    }
  };

  const filterRow = (iso) => {
    if (filtMese && monthFromISO(iso) !== filtMese) return false;
    if (filtAnno && String(yearFromISO(iso)) !== String(filtAnno)) return false;
    if (filtGiorno && !iso?.startsWith(filtGiorno)) return false;
    return true;
  };
  const fatt = useMemo(() => (detail?.fatture || []).filter((f) => filterRow(f.data)), [detail, filtMese, filtAnno, filtGiorno]);
  const ddts = useMemo(() => (detail?.ddt || []).filter((d) => filterRow(d.data)), [detail, filtMese, filtAnno, filtGiorno]);

  const annoOptions = useMemo(() => {
    const s = new Set();
    (detail?.fatture || []).forEach((f) => { const y = yearFromISO(f.data); if (y) s.add(y); });
    (detail?.ddt || []).forEach((d) => { const y = yearFromISO(d.data); if (y) s.add(y); });
    return Array.from(s).sort((a, b) => b - a);
  }, [detail]);

  const cantieriById = Object.fromEntries(cantieri.map((c) => [c.id, c.nome]));
  const fornitoriById = Object.fromEntries(list.map((f) => [f.id, f.nome]));

  // Filtro DDT globali
  const ddtFiltered = useMemo(() => {
    return (allDdt || []).filter((d) => {
      if (ddtFiltData && !(d.data || "").startsWith(ddtFiltData)) return false;
      if (ddtFiltForn === "__none__") { if (d.fornitore_id) return false; }
      else if (ddtFiltForn && d.fornitore_id !== ddtFiltForn) return false;
      if (ddtFiltCant === "__none__") { if (d.cantiere_id) return false; }
      else if (ddtFiltCant && d.cantiere_id !== ddtFiltCant) return false;
      if (ddtFiltStato === "fatt") { if (!d.fattura_id) return false; }
      else if (ddtFiltStato === "nofatt") { if (d.fattura_id) return false; }
      if (ddtFiltPag && (d.stato_pagamento_effettivo || d.stato_pagamento || "non_pagato") !== ddtFiltPag) return false;
      return true;
    });
  }, [allDdt, ddtFiltData, ddtFiltForn, ddtFiltCant, ddtFiltStato, ddtFiltPag]);
  const selVis = selDdt.filter((id) => ddtFiltered.some((d) => d.id === id));

  const openEditDdt = (d) => {
    setEditingDdtId(d.id);
    setEditDdtForm({
      numero: d.numero || "",
      data: d.data || new Date().toISOString().slice(0, 10),
      descrizione: d.descrizione || "",
      importo_stimato: Number(d.importo_stimato) || 0,
      cantiere_id: d.cantiere_id || null,
      fornitore_id: d.fornitore_id || null,
    });
  };
  const saveEditDdt = async () => {
    try {
      await api.put(`/ddt/${editingDdtId}`, editDdtForm);
      toast.success("DDT aggiornato");
      setEditingDdtId(null); loadAllDdt();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore aggiornamento"); }
  };
  const deleteDdt = async (did) => {
    if (!window.confirm("Eliminare questo DDT?")) return;
    try { await api.delete(`/ddt/${did}`); toast.success("DDT eliminato"); loadAllDdt(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Rubrica</div>
          <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight">Fornitori</h1>
          <p className="text-muted-foreground mt-1">Clicca su un fornitore per vedere fatture, DDT e aggiungerne di nuovi.</p>
        </div>
        <Button data-testid="new-fornitore" className="h-11" onClick={() => setChoiceOpen(true)}><Plus className="h-4 w-4 mr-2" /> Nuovo fornitore</Button>
      </div>
      <NuovoFornitoreChoice open={choiceOpen} onOpenChange={setChoiceOpen}
        onManual={() => { setChoiceOpen(false); setEditor({ initial: null, id: null }); }}
        onExtracted={(d) => { setChoiceOpen(false); setEditor({ initial: d, id: null }); }} />
      <FornitoreEditor open={!!editor} onOpenChange={(o) => !o && setEditor(null)} initial={editor?.initial} editingId={editor?.id}
        onSaved={() => { load(); refreshDetail(); }} />

      <Tabs value={mainTab} onValueChange={setMainTab} className="w-full" data-testid="fornitori-main-tabs">
        <TabsList>
          <TabsTrigger value="rubrica" data-testid="tab-rubrica"><Building2 className="h-3.5 w-3.5 mr-1" /> Rubrica fornitori</TabsTrigger>
          <TabsTrigger value="ddt" data-testid="tab-tutti-ddt"><Truck className="h-3.5 w-3.5 mr-1" /> Tutti i DDT ({allDdt.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="rubrica" className="pt-4 space-y-4">
      <div className="relative w-full sm:w-80">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input className="pl-8" placeholder="Cerca fornitore per nome o P.IVA" value={searchForn} onChange={(e) => setSearchForn(e.target.value)} data-testid="fornitori-search" />
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {fornFiltered.map((f) => (
          <Card key={f.id} className="card-elevated hover:border-primary cursor-pointer" onClick={() => openDetail(f.id)} data-testid={`fornitore-${f.id}`}>
            <CardContent className="p-5 space-y-2">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2"><Building2 className="h-4 w-4 text-primary" /><div className="font-bold">{f.nome}</div></div>
                  {f.partita_iva && <div className="text-xs text-muted-foreground">P.IVA {f.partita_iva}</div>}
                </div>
                <div className="flex">
                  <Button variant="ghost" size="icon" title="Modifica anagrafica" onClick={(e) => { e.stopPropagation(); setEditor({ initial: f, id: f.id }); }} data-testid={`edit-fornitore-${f.id}`}><Pencil className="h-3 w-3" /></Button>
                  <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); remove(f.id); }}><Trash2 className="h-3 w-3 text-destructive" /></Button>
                </div>
              </div>
              {f.telefono && <div className="flex items-center gap-2 text-xs text-muted-foreground"><Phone className="h-3 w-3" />{f.telefono}</div>}
              {f.email && <div className="flex items-center gap-2 text-xs text-muted-foreground"><Mail className="h-3 w-3" />{f.email}</div>}
              {f.iban && <CopiaIban iban={f.iban} testid={`copy-iban-${f.id}`} />}
              <div className="flex items-center gap-3 pt-2 text-xs border-t border-border">
                <Badge variant="secondary" className="text-[10px]"><Receipt className="h-3 w-3 mr-1" />{f.n_fatture || 0} fatture</Badge>
                <Badge variant="secondary" className="text-[10px]"><FileText className="h-3 w-3 mr-1" />{f.n_ddt || 0} DDT</Badge>
                <span className="font-bold tabular-nums ml-auto">{fmt(f.totale_acquisti || 0)}</span>
              </div>
            </CardContent>
          </Card>
        ))}
        {fornFiltered.length === 0 && list.length > 0 && <div className="text-muted-foreground col-span-full py-12 text-center" data-testid="fornitori-search-empty">Nessun fornitore trovato.</div>}
        {list.length === 0 && <div className="text-muted-foreground col-span-full py-12 text-center">Nessun fornitore. Aggiungi il primo.</div>}
      </div>
        </TabsContent>

        <TabsContent value="ddt" className="pt-4 space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => setAggiungiOpen(true)} className="h-11" data-testid="aggiungi-ddt-btn"><Plus className="h-4 w-4 mr-2" /> Aggiungi DDT</Button>
          </div>
          <Card className="card-elevated">
            <CardContent className="p-4">
              <div className="flex items-end gap-2 flex-wrap bg-secondary/50 rounded-lg p-3 mb-3">
                <div className="flex items-center gap-1 text-xs font-semibold text-muted-foreground mr-1">
                  <Filter className="h-3 w-3" /> Filtri
                </div>
                <div>
                  <Label className="text-[10px]">Data</Label>
                  <Input data-testid="ddtg-filtro-data" type="date" value={ddtFiltData} onChange={(e) => setDdtFiltData(e.target.value)} className="h-8 w-40 text-xs" />
                </div>
                <div>
                  <Label className="text-[10px]">Fornitore</Label>
                  <Select value={ddtFiltForn || "all"} onValueChange={(v) => setDdtFiltForn(v === "all" ? "" : v)}>
                    <SelectTrigger data-testid="ddtg-filtro-forn" className="h-8 w-44 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tutti i fornitori</SelectItem>
                      <SelectItem value="__none__">— Non assegnato —</SelectItem>
                      {list.map((f) => <SelectItem key={f.id} value={f.id}>{f.nome}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px]">Cantiere</Label>
                  <Select value={ddtFiltCant || "all"} onValueChange={(v) => setDdtFiltCant(v === "all" ? "" : v)}>
                    <SelectTrigger data-testid="ddtg-filtro-cant" className="h-8 w-44 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tutti i cantieri</SelectItem>
                      <SelectItem value="__none__">— Non assegnato —</SelectItem>
                      {cantieri.map((c) => <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px]">Stato</Label>
                  <Select value={ddtFiltStato || "all"} onValueChange={(v) => setDdtFiltStato(v === "all" ? "" : v)}>
                    <SelectTrigger data-testid="ddtg-filtro-stato" className="h-8 w-32 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tutti</SelectItem>
                      <SelectItem value="nofatt">Non fatturati</SelectItem>
                      <SelectItem value="fatt">Fatturati</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px]">Pagamento</Label>
                  <Select value={ddtFiltPag || "all"} onValueChange={(v) => setDdtFiltPag(v === "all" ? "" : v)}>
                    <SelectTrigger data-testid="ddtg-filtro-pag" className="h-8 w-36 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tutti</SelectItem>
                      <SelectItem value="non_pagato">Non pagato</SelectItem>
                      <SelectItem value="parziale">Parziale</SelectItem>
                      <SelectItem value="pagato">Pagato</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {(ddtFiltData || ddtFiltForn || ddtFiltCant || ddtFiltStato || ddtFiltPag) && (
                  <Button size="sm" variant="ghost" onClick={() => { setDdtFiltData(""); setDdtFiltForn(""); setDdtFiltCant(""); setDdtFiltStato(""); setDdtFiltPag(""); }}>Pulisci</Button>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-2 mb-2" data-testid="ddtg-bulk-bar">
                <Button size="sm" variant="outline" onClick={() => setBulkLink(ddtFiltered.filter((d) => !d.fattura_id).map((d) => d.id))}
                  disabled={!ddtFiltered.some((d) => !d.fattura_id)} data-testid="ddtg-collega-tutti">
                  <Link2 className="h-3.5 w-3.5 mr-1" /> Collega tutti i non collegati ({ddtFiltered.filter((d) => !d.fattura_id).length})
                </Button>
                {selVis.length > 0 && (
                  <>
                    <span className="text-xs text-muted-foreground" data-testid="ddtg-sel-count">{selVis.length} selezionati</span>
                    <Button size="sm" onClick={() => setBulkLink(selVis.filter((id) => !allDdt.find((x) => x.id === id)?.fattura_id))}
                      disabled={!selVis.some((id) => !allDdt.find((x) => x.id === id)?.fattura_id)} data-testid="ddtg-collega-selezionati">
                      <Link2 className="h-3.5 w-3.5 mr-1" /> Collega selezionati
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setSelDdt([])} data-testid="ddtg-sel-clear">Deseleziona</Button>
                  </>
                )}
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-secondary">
                    <tr className="text-left">
                      <th className="px-3 py-2 w-8">
                        <input type="checkbox" checked={ddtFiltered.length > 0 && ddtFiltered.every((d) => selDdt.includes(d.id))}
                          onChange={(e) => setSelDdt((s) => (e.target.checked ? [...new Set([...s, ...ddtFiltered.map((d) => d.id)])] : s.filter((id) => !ddtFiltered.some((d) => d.id === id))))} data-testid="ddtg-sel-all" />
                      </th>
                      <th className="px-3 py-2 text-[10px] uppercase tracking-widest text-muted-foreground">Data</th>
                      <th className="px-3 py-2 text-[10px] uppercase tracking-widest text-muted-foreground">Numero</th>
                      <th className="px-3 py-2 text-[10px] uppercase tracking-widest text-muted-foreground">Fornitore</th>
                      <th className="px-3 py-2 text-[10px] uppercase tracking-widest text-muted-foreground">Cantiere</th>
                      <th className="px-3 py-2 text-[10px] uppercase tracking-widest text-muted-foreground">Descrizione</th>
                      <th className="px-3 py-2 text-[10px] uppercase tracking-widest text-muted-foreground">Importo</th>
                      <th className="px-3 py-2 text-[10px] uppercase tracking-widest text-muted-foreground">Fatturato</th>
                      <th className="px-3 py-2 text-[10px] uppercase tracking-widest text-muted-foreground">Pagamento</th>
                      <th className="px-3 py-2 text-[10px] uppercase tracking-widest text-muted-foreground text-right">Azioni</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ddtFiltered.map((d) => {
                      const effPag = d.stato_pagamento_effettivo || d.stato_pagamento || "non_pagato";
                      return (
                      <tr key={d.id} className={`border-t border-border ${highlightDdt === d.id ? "bg-primary/10" : selDdt.includes(d.id) ? "bg-primary/5" : ""}`} data-testid={`ddt-global-row-${d.id}`}>
                        <td className="px-3 py-2">
                          <input type="checkbox" checked={selDdt.includes(d.id)} onChange={(e) => setSelDdt((s) => (e.target.checked ? [...s, d.id] : s.filter((x) => x !== d.id)))} data-testid={`ddtg-sel-${d.id}`} />
                        </td>
                        <td className="px-3 py-2">{d.data}</td>
                        <td className="px-3 py-2">{d.numero || "—"}</td>
                        <td className="px-3 py-2 text-xs">
                          {d.fornitore_id
                            ? (fornitoriById[d.fornitore_id] || <span className="text-amber-600">ID {d.fornitore_id.slice(0,6)}</span>)
                            : <Badge variant="outline" className="text-[10px]">Non assegnato</Badge>}
                        </td>
                        <td className="px-3 py-2 text-xs">
                          {d.cantiere_id
                            ? (cantieriById[d.cantiere_id] || "—")
                            : <Badge variant="outline" className="text-[10px]">Non assegnato</Badge>}
                        </td>
                        <td className="px-3 py-2 text-xs max-w-xs truncate">{d.descrizione}</td>
                        <td className="px-3 py-2 tabular-nums">{d.totale ? (<><div>{fmt(d.totale)}</div><div className="text-[10px] text-muted-foreground">imp. {fmt(d.importo_stimato)} + IVA {fmt(d.iva_importo)}</div></>) : d.importo_stimato ? fmt(d.importo_stimato) : "—"}</td>
                        <td className="px-3 py-2">{d.fattura_id
                          ? <button type="button" onClick={() => openFatturaLink(d.fattura_id)} data-testid={`ddt-fattura-link-${d.id}`}><Badge className="text-[10px] cursor-pointer">{d.fattura_numero ? `#${d.fattura_numero}` : "sì"} <ExternalLink className="h-2.5 w-2.5 ml-1" /></Badge></button>
                          : <button type="button" onClick={() => setLinkDdt(d.id)} title="Collega a una fattura" data-testid={`ddt-link-fattura-${d.id}`}><Badge variant="destructive" className="text-[10px] cursor-pointer">no <Link2 className="h-2.5 w-2.5 ml-1" /></Badge></button>}</td>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-1 flex-wrap">
                            <Badge className={`text-[10px] whitespace-nowrap ${DDT_PAG_COLORS[effPag]}`} data-testid={`ddt-pag-${d.id}`}>{DDT_PAG_LABELS[effPag]}</Badge>
                            {d.ereditato && d.fattura_numero && (
                              <Badge variant="outline" className="text-[9px] border-sky-400 text-sky-700" title={`Ereditato da fattura ${d.fattura_numero}`}>ered. #{d.fattura_numero}</Badge>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-right whitespace-nowrap">
                          {d.allegato_filename && (
                            <a href={`${API}/ddt/${d.id}/allegato`} target="_blank" rel="noreferrer" title={`Scarica ${d.allegato_filename}`}>
                              <Button variant="ghost" size="icon"><Download className="h-3 w-3 text-emerald-600" /></Button>
                            </a>
                          )}
                          <Button variant="ghost" size="icon" title="Registra pagamento" onClick={() => setPayDdt(d.id)} data-testid={`pay-ddt-${d.id}`}>
                            <Wallet className="h-3 w-3 text-emerald-600" />
                          </Button>
                          <Button variant="ghost" size="icon" title="Modifica" onClick={() => openEditDdt(d)} data-testid={`edit-ddt-${d.id}`}>
                            <Pencil className="h-3 w-3" />
                          </Button>
                          <Button variant="ghost" size="icon" title="Elimina" onClick={() => deleteDdt(d.id)}>
                            <Trash2 className="h-3 w-3 text-destructive" />
                          </Button>
                        </td>
                      </tr>
                      );
                    })}
                    {ddtFiltered.length === 0 && <tr><td colSpan={10} className="px-3 py-10 text-center text-muted-foreground"><Truck className="h-6 w-6 mx-auto mb-2 opacity-50" /> Nessun DDT per i filtri scelti.</td></tr>}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <AggiungiDdtDialog open={aggiungiOpen} onOpenChange={setAggiungiOpen} fornitori={list} cantieri={cantieri}
        onSaved={() => { loadAllDdt(); load(); }} onOpenFattura={setDdtDialogFid} />
      <FatturaDdtDialog fid={ddtDialogFid} onClose={() => setDdtDialogFid(null)} onChanged={() => { loadAllDdt(); refreshDetail(); }} />
      <BulkCollegaDdtDialog ids={bulkLink} onClose={() => setBulkLink([])} onDone={() => { setSelDdt([]); loadAllDdt(); refreshDetail(); }} />
      <CollegaDdtFatturaDialog did={linkDdt} onClose={() => setLinkDdt(null)} onDone={() => { loadAllDdt(); refreshDetail(); }} />
      <DdtPagamentoDialog did={payDdt} onClose={() => setPayDdt(null)} onSaved={() => { loadAllDdt(); refreshDetail(); }} />
      <DdtRiferitiDialog fattura={riferiti?.fattura} items={riferiti?.items} onClose={() => setRiferiti(null)} onDone={() => { loadAllDdt(); refreshDetail(); }} />

      {/* Dialog modifica DDT */}
      <Dialog open={!!editingDdtId} onOpenChange={(o) => !o && setEditingDdtId(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Modifica DDT</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Numero</Label><Input data-testid="edit-ddt-numero" value={editDdtForm.numero} onChange={(e) => setEditDdtForm({ ...editDdtForm, numero: e.target.value })} /></div>
            <div><Label>Data</Label><Input type="date" value={editDdtForm.data} onChange={(e) => setEditDdtForm({ ...editDdtForm, data: e.target.value })} /></div>
            <div className="col-span-2"><Label>Fornitore</Label>
              <Select value={editDdtForm.fornitore_id || "__none__"} onValueChange={(v) => setEditDdtForm({ ...editDdtForm, fornitore_id: v === "__none__" ? null : v })}>
                <SelectTrigger data-testid="edit-ddt-forn"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— Non assegnato —</SelectItem>
                  {list.map((f) => <SelectItem key={f.id} value={f.id}>{f.nome}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label>Cantiere</Label>
              <Select value={editDdtForm.cantiere_id || "__none__"} onValueChange={(v) => setEditDdtForm({ ...editDdtForm, cantiere_id: v === "__none__" ? null : v })}>
                <SelectTrigger data-testid="edit-ddt-cant"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— Non assegnato —</SelectItem>
                  {cantieri.map((c) => <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label>Descrizione</Label><Textarea rows={2} value={editDdtForm.descrizione} onChange={(e) => setEditDdtForm({ ...editDdtForm, descrizione: e.target.value })} /></div>
            <div className="col-span-2"><Label>Importo stimato €</Label><Input type="number" step="0.01" value={editDdtForm.importo_stimato} onChange={(e) => setEditDdtForm({ ...editDdtForm, importo_stimato: Number(e.target.value) })} /></div>
          </div>
          <Button data-testid="edit-ddt-save" onClick={saveEditDdt} className="h-11 mt-2">Salva modifiche</Button>
        </DialogContent>
      </Dialog>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center justify-between gap-3 flex-wrap">
                  <span>{detail.fornitore.nome}</span>
                  <div className="flex items-center gap-2 flex-wrap">
                    {detail.fornitore.iban && <CopiaIban iban={detail.fornitore.iban} testid="forn-detail-copy-iban" />}
                    <Button size="sm" variant="outline" onClick={() => setEditor({ initial: detail.fornitore, id: detail.fornitore.id })} data-testid="forn-edit-anagrafica">
                      <Pencil className="h-3 w-3 mr-1" /> Modifica anagrafica
                    </Button>
                    <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden"
                           onChange={(e) => onPhoto(e.target.files?.[0])} data-testid="forn-ocr-file" />
                    <Button size="sm" variant="outline" disabled={ocrBusy} onClick={() => fileRef.current?.click()} data-testid="forn-ocr-btn">
                      {ocrBusy ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Camera className="h-3 w-3 mr-1" />}
                      Scatta foto (AI)
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => { setFattForm({ ...emptyFatt }); setAddDoc("fattura"); }} data-testid="add-fattura-from-forn">
                      <Plus className="h-3 w-3 mr-1" /> Fattura
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => { setDdtForm({ ...emptyDDT }); setPagNew(PAG_DDT_EMPTY); setAddDoc("ddt"); }} data-testid="add-ddt-from-forn">
                      <Plus className="h-3 w-3 mr-1" /> DDT
                    </Button>
                    <a href={`${API}/fornitori/${detail.fornitore.id}/excel`} target="_blank" rel="noreferrer" data-testid="dl-excel-forn">
                      <Button size="sm"><Download className="h-3 w-3 mr-1" /> Excel</Button>
                    </a>
                  </div>
                </DialogTitle>
              </DialogHeader>

              {/* Filtri */}
              <div className="flex items-end gap-2 flex-wrap bg-secondary/50 rounded-lg p-3">
                <div className="flex items-center gap-1 text-xs font-semibold text-muted-foreground mr-1">
                  <Filter className="h-3 w-3" /> Filtri
                </div>
                <div>
                  <Label className="text-[10px]">Mese</Label>
                  <Select value={String(filtMese)} onValueChange={(v) => setFiltMese(parseInt(v, 10))}>
                    <SelectTrigger data-testid="filtro-mese" className="h-8 w-32 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {MESI.map((m, i) => <SelectItem key={i} value={String(i)}>{m}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px]">Anno</Label>
                  <Select value={String(filtAnno || "all")} onValueChange={(v) => setFiltAnno(v === "all" ? "" : v)}>
                    <SelectTrigger data-testid="filtro-anno" className="h-8 w-24 text-xs"><SelectValue placeholder="Tutti" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tutti</SelectItem>
                      {annoOptions.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px]">Giorno preciso</Label>
                  <Input data-testid="filtro-giorno" type="date" value={filtGiorno} onChange={(e) => setFiltGiorno(e.target.value)} className="h-8 w-40 text-xs" />
                </div>
                {(filtMese || filtAnno || filtGiorno) && (
                  <Button size="sm" variant="ghost" onClick={() => { setFiltMese(0); setFiltAnno(""); setFiltGiorno(""); }}>Pulisci</Button>
                )}
              </div>

              <Tabs defaultValue="fatture">
                <TabsList>
                  <TabsTrigger value="fatture">Fatture ({fatt.length}{(filtMese || filtAnno || filtGiorno) ? `/${detail.fatture.length}` : ""})</TabsTrigger>
                  <TabsTrigger value="ddt">DDT ({ddts.length}{(filtMese || filtAnno || filtGiorno) ? `/${detail.ddt.length}` : ""})</TabsTrigger>
                </TabsList>
                <TabsContent value="fatture" className="pt-3 max-h-[50vh] overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-secondary sticky top-0">
                      <tr className="text-left">
                        <th className="px-3 py-2 text-[10px] uppercase tracking-widest text-muted-foreground">Data</th>
                        <th className="px-3 py-2 text-[10px] uppercase tracking-widest text-muted-foreground">Numero</th>
                        <th className="px-3 py-2 text-[10px] uppercase tracking-widest text-muted-foreground">Cantiere</th>
                        <th className="px-3 py-2 text-[10px] uppercase tracking-widest text-muted-foreground">Totale</th>
                        <th className="px-3 py-2 text-[10px] uppercase tracking-widest text-muted-foreground">Stato</th>
                        <th className="px-3 py-2 text-[10px] uppercase tracking-widest text-muted-foreground">Mezzo</th>
                        <th className="px-3 py-2 text-[10px] uppercase tracking-widest text-muted-foreground text-right">DDT</th>
                      </tr>
                    </thead>
                    <tbody>
                      {fatt.map((f) => (
                        <tr key={f.id} className="border-t border-border">
                          <td className="px-3 py-2">{f.data}</td>
                          <td className="px-3 py-2">{f.numero}</td>
                          <td className="px-3 py-2 text-xs text-muted-foreground">{cantieriById[f.cantiere_id] || "—"}</td>
                          <td className="px-3 py-2 tabular-nums font-bold">{fmt(f.totale)}</td>
                          <td className="px-3 py-2"><Badge variant={f.stato === "pagata" ? "default" : "destructive"} className="text-[10px]">{f.stato}</Badge></td>
                          <td className="px-3 py-2 text-xs text-muted-foreground">{f.mezzo_pagamento || "—"}</td>
                          <td className="px-3 py-2 text-right">
                            <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => setDdtDialogFid(f.id)} data-testid={`forn-fatt-ddt-${f.id}`}>
                              <Truck className="h-3 w-3" />{(f.ddt_ids || []).length > 0 && <span className="ml-1 text-[10px] font-bold">{f.ddt_ids.length}</span>}
                            </Button>
                          </td>
                        </tr>
                      ))}
                      {fatt.length === 0 && <tr><td colSpan={7} className="px-3 py-6 text-center text-muted-foreground">Nessuna fattura per i filtri scelti.</td></tr>}
                    </tbody>
                  </table>
                </TabsContent>
                <TabsContent value="ddt" className="pt-3 max-h-[50vh] overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-secondary sticky top-0">
                      <tr className="text-left">
                        <th className="px-3 py-2 text-[10px] uppercase tracking-widest text-muted-foreground">Data</th>
                        <th className="px-3 py-2 text-[10px] uppercase tracking-widest text-muted-foreground">Numero</th>
                        <th className="px-3 py-2 text-[10px] uppercase tracking-widest text-muted-foreground">Cantiere</th>
                        <th className="px-3 py-2 text-[10px] uppercase tracking-widest text-muted-foreground">Descrizione</th>
                        <th className="px-3 py-2 text-[10px] uppercase tracking-widest text-muted-foreground">Importo</th>
                        <th className="px-3 py-2 text-[10px] uppercase tracking-widest text-muted-foreground">Fatturato</th>
                        <th className="px-3 py-2 text-[10px] uppercase tracking-widest text-muted-foreground">Pagamento</th>
                        <th className="px-3 py-2 text-[10px] uppercase tracking-widest text-muted-foreground text-right">Doc.</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ddts.map((d) => (
                        <tr key={d.id} className="border-t border-border">
                          <td className="px-3 py-2">{d.data}</td>
                          <td className="px-3 py-2">{d.numero || "—"}</td>
                          <td className="px-3 py-2 text-xs text-muted-foreground">{cantieriById[d.cantiere_id] || "—"}</td>
                          <td className="px-3 py-2 text-xs">{d.descrizione}</td>
                          <td className="px-3 py-2 tabular-nums">{d.totale ? (<><div>{fmt(d.totale)}</div><div className="text-[10px] text-muted-foreground">imp. {fmt(d.importo_stimato)} + IVA {fmt(d.iva_importo)}</div></>) : d.importo_stimato ? fmt(d.importo_stimato) : "—"}</td>
                          <td className="px-3 py-2">{d.fattura_id
                            ? <button type="button" onClick={() => { setDetail(null); openFatturaLink(d.fattura_id); }} data-testid={`forn-ddt-fattura-link-${d.id}`}><Badge className="text-[10px] cursor-pointer">sì <ExternalLink className="h-2.5 w-2.5 ml-1" /></Badge></button>
                            : <button type="button" onClick={() => setLinkDdt(d.id)} title="Collega a una fattura" data-testid={`forn-ddt-link-fattura-${d.id}`}><Badge variant="destructive" className="text-[10px] cursor-pointer">no <Link2 className="h-2.5 w-2.5 ml-1" /></Badge></button>}</td>
                          <td className="px-3 py-2">{(() => { const e = allDdt.find((x) => x.id === d.id); const st = e?.stato_pagamento_effettivo || d.stato_pagamento || "non_pagato"; return <Badge className={`text-[10px] ${DDT_PAG_COLORS[st]}`} data-testid={`forn-ddt-pag-${d.id}`}>{DDT_PAG_LABELS[st]}</Badge>; })()}</td>
                          <td className="px-3 py-2 text-right whitespace-nowrap">
                            <Button variant="ghost" size="icon" title="Registra pagamento" onClick={() => setPayDdt(d.id)} data-testid={`forn-pay-ddt-${d.id}`}><Wallet className="h-3 w-3 text-emerald-600" /></Button>
                            {d.allegato_filename ? (
                              <a href={`${API}/ddt/${d.id}/allegato`} target="_blank" rel="noreferrer" title={`Scarica ${d.allegato_filename}`}>
                                <Button variant="ghost" size="icon" data-testid={`dl-ddt-allegato-${d.id}`}>
                                  <Download className="h-3 w-3 text-emerald-600" />
                                </Button>
                              </a>
                            ) : (
                              <span className="text-[10px] text-muted-foreground">—</span>
                            )}
                          </td>
                        </tr>
                      ))}
                      {ddts.length === 0 && <tr><td colSpan={8} className="px-3 py-6 text-center text-muted-foreground">Nessun DDT per i filtri scelti.</td></tr>}
                    </tbody>
                  </table>
                </TabsContent>
              </Tabs>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog conferma tipo OCR */}
      <Dialog open={!!ocrResult} onOpenChange={(o) => !o && setOcrResult(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-primary" /> Che documento è?</DialogTitle></DialogHeader>
          {ocrResult && (
            <div className="space-y-3">
              <div className="bg-secondary rounded-lg p-3 text-sm space-y-1">
                <div className="text-xs text-muted-foreground">L'AI ha riconosciuto:</div>
                <div className="font-bold text-lg">
                  {ocrResult.tipo_rilevato === "fattura" ? "📄 Fattura" : "🚚 DDT (bolla)"}
                  <span className="text-xs font-normal text-muted-foreground ml-2">({ocrResult.confidenza || 0}% confidenza)</span>
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  {ocrResult.tipo_rilevato === "fattura"
                    ? `Numero: ${ocrResult.fattura?.numero || "—"} · Totale: ${fmt(ocrResult.fattura?.totale || 0)}`
                    : `Numero: ${ocrResult.ddt?.numero || "—"} · ${ocrResult.ddt?.descrizione || "—"}`}
                </div>
                {ocrResult.tipo_rilevato === "fattura" && <OcrBadges o={ocrResult.fattura} />}
              </div>
              <div className="text-xs text-muted-foreground">Conferma o cambia il tipo:</div>
              <div className="grid grid-cols-2 gap-2">
                <Button data-testid="ocr-confirm-fattura" variant={ocrResult.tipo_rilevato === "fattura" ? "default" : "outline"} onClick={() => confirmOcrType("fattura")}>
                  <Receipt className="h-4 w-4 mr-2" /> È una Fattura
                </Button>
                <Button data-testid="ocr-confirm-ddt" variant={ocrResult.tipo_rilevato === "ddt" ? "default" : "outline"} onClick={() => confirmOcrType("ddt")}>
                  <FileText className="h-4 w-4 mr-2" /> È un DDT
                </Button>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setOcrResult(null)} className="w-full">Annulla</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog Fattura */}
      <Dialog open={addDoc === "fattura"} onOpenChange={(o) => !o && setAddDoc(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Nuova fattura per {detail?.fornitore?.nome}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Numero</Label><Input data-testid="fatt-numero" value={fattForm.numero} onChange={(e) => setFattForm({ ...fattForm, numero: e.target.value })} /></div>
            <div><Label>Data</Label><Input type="date" value={fattForm.data} onChange={(e) => setFattForm({ ...fattForm, data: e.target.value })} /></div>
            <div><Label>Cantiere</Label>
              <Select value={fattForm.cantiere_id || "none"} onValueChange={(v) => setFattForm({ ...fattForm, cantiere_id: v === "none" ? null : v })}>
                <SelectTrigger><SelectValue placeholder="Seleziona" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Nessuno —</SelectItem>
                  {cantieri.map((c) => <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Mezzo pagamento</Label>
              <Select value={fattForm.mezzo_pagamento} onValueChange={(v) => setFattForm({ ...fattForm, mezzo_pagamento: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="bonifico">Bonifico</SelectItem>
                  <SelectItem value="assegno">Assegno</SelectItem>
                  <SelectItem value="contanti">Contanti</SelectItem>
                  <SelectItem value="pos">POS</SelectItem>
                  <SelectItem value="ricevuta_bancaria">Ricevuta bancaria</SelectItem>
                  <SelectItem value="altro">Altro</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Imponibile €</Label><Input data-testid="fatt-imp" type="number" step="0.01" value={fattForm.imponibile} onChange={(e) => setFattForm({ ...fattForm, imponibile: Number(e.target.value) })} /></div>
            <div><Label>IVA %</Label><Input type="number" value={fattForm.iva_percentuale} onChange={(e) => setFattForm({ ...fattForm, iva_percentuale: Number(e.target.value) })} /></div>
            <div><Label>Scadenza</Label><Input type="date" value={fattForm.data_scadenza} onChange={(e) => setFattForm({ ...fattForm, data_scadenza: e.target.value })} /></div>
            <div><Label>Stato</Label>
              <Select value={fattForm.stato} onValueChange={(v) => setFattForm({ ...fattForm, stato: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="da_pagare">Da pagare</SelectItem>
                  <SelectItem value="parziale">Parziale</SelectItem>
                  <SelectItem value="pagata">Pagata</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label>Note</Label><Textarea rows={2} value={fattForm.note} onChange={(e) => setFattForm({ ...fattForm, note: e.target.value })} /></div>
          </div>
          <Button data-testid="fatt-save" onClick={saveFatt} className="h-11 mt-2">Salva fattura</Button>
        </DialogContent>
      </Dialog>

      {/* Dialog DDT */}
      <Dialog open={addDoc === "ddt"} onOpenChange={(o) => !o && setAddDoc(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Nuovo DDT per {detail?.fornitore?.nome}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Numero</Label><Input data-testid="ddt-numero" value={ddtForm.numero} onChange={(e) => setDdtForm({ ...ddtForm, numero: e.target.value })} /></div>
            <div><Label>Data</Label><Input type="date" value={ddtForm.data} onChange={(e) => setDdtForm({ ...ddtForm, data: e.target.value })} /></div>
            <div className="col-span-2"><Label>Cantiere</Label>
              <Select value={ddtForm.cantiere_id || "none"} onValueChange={(v) => setDdtForm({ ...ddtForm, cantiere_id: v === "none" ? null : v })}>
                <SelectTrigger data-testid="ddt-cantiere"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Da assegnare —</SelectItem>
                  {cantieri.map((c) => <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label>Descrizione</Label><Textarea data-testid="ddt-descr" rows={2} value={ddtForm.descrizione} onChange={(e) => setDdtForm({ ...ddtForm, descrizione: e.target.value })} /></div>
            <div className="col-span-2"><Label>Importo stimato € (se noto)</Label><Input type="number" step="0.01" value={ddtForm.importo_stimato} onChange={(e) => setDdtForm({ ...ddtForm, importo_stimato: Number(e.target.value) })} /></div>
            <div className="col-span-2 border-t pt-2 mt-1">
              <DdtPagamentoFields value={pagNew} onChange={setPagNew} testid="forn-ddtnew" />
            </div>
          </div>
          <Button data-testid="ddt-save" onClick={saveDDT} className="h-11 mt-2">Salva DDT</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
